import React, { useCallback, useEffect, useRef, useState } from "react";
import i18next from "i18next";
import axios from "axios";
import { Feature } from "ol";
import { useMapContext } from "../../Contexts/MapContext";
import { useSearchContext } from "../../Contexts/SearchContext";
import { createFeatureGroup, extendFeatures, ILayerConfig, replaceFieldsWithValues } from "./SearchUtils";
import { IFeatureGroup } from "@geo/js";
import { buildQueries, executeDefaultQuery, executeOracleTextSearchQuery } from "./searchQueries";

export const SearchInput = (): JSX.Element => {
    const { configA } = useMapContext();
    const {
        searchParam,
        setSearchParam,
        resultFeatures,
        setResultFeatures,
        loading,
        setLoading,
        setSearchHelp,
        setSelected,
        selectedGroup,
        setError,
        setNotification
    } = useSearchContext();

    const [layerInfos, setLayerInfos] = useState<[]>([]);

    const resultFeaturesRef = useRef<IFeatureGroup[]>([]);

    useEffect(() => {
        resultFeaturesRef.current = resultFeatures;
    }, [resultFeatures]);

    const executeSearch = useCallback(async () => {
        if (!searchParam || loading) {
            return;
        }
        setLoading(true);
        setResultFeatures([]);
        setLoading(true);
        setSearchHelp(false);
        setError("");
        const queries = buildQueries(searchParam, configA, selectedGroup, onError);
        await doQuery(queries.flat());
        setLoading(false);
    }, [searchParam, selectedGroup, loading]);

    const onKeyDown = (e) => e.key === "Enter" && executeSearch();

    const onClear = () => {
        setSearchParam("");
        setSelected(undefined);
    };

    const clearResults = () => {
        setResultFeatures([]);
        setSearchHelp(true);
    };

    const onChange = (e) => {
        setSelected(undefined);
        setSearchParam(e.target.value);
    };

    const onError = () => {
        setLoading(false);
        setError(i18next.t("searchPanel.inputDoesntMatch"));
    };

    const doQuery = async (queries) => {
        if (!queries) {
            return [];
        }

        const results = [];
        for (let i = 0; i < queries.length; i++) {
            const query = queries[i];
            const features =
                query.layer?.type === "oracleTextSearch"
                    ? await executeOracleTextSearchQuery(query.url, query.layer, query.searchParam)
                    : await executeDefaultQuery(query.url, query.layer, query.searchParam);

            const isLastQuery = i === queries.length - 1;
            ((features && features.length) || isLastQuery) && addResults(features, query.layer, false, isLastQuery);
        }
        return results;
    };

    const addResults = async (results: Feature[], layerConfig, sortGroup?: boolean, isLastQuery?: boolean) => {
        layerConfig.layerInfo = await getLayerInfo(layerConfig);
        const features = extendFeatures(results, layerConfig);
        const featureGroup = createFeatureGroup(layerConfig, features, sortGroup);

        const resultFeaturesNew = [...resultFeaturesRef.current, featureGroup];

        resultFeaturesNew.forEach((resultFeature) => {
            if (resultFeature.features && resultFeature.features.length === 1000) {
                setNotification(i18next.t("searchPanel.reachedMax"));
                setTimeout(() => {
                    setNotification("");
                }, 3000);
            }
        });
        setResultFeatures(resultFeaturesNew);
        isLastQuery && setLoading(false);
    };

    const getLayerInfo = async (layerConfig: ILayerConfig) => {
        const layerInfoId = layerConfig.serviceId + "_" + layerConfig.id;
        if (layerInfos[layerInfoId]) {
            return layerInfos[layerInfoId];
        }

        const newLeayerInfo = layerInfos;
        const response = await axios(layerConfig.url + "/?f=json");
        newLeayerInfo[layerInfoId] = response.data;
        setLayerInfos(newLeayerInfo);

        return response.data;
    };

    return (
        <div className="search-bar">
            <div className="search-input">
                <input
                    type="text"
                    placeholder={i18next.t("searchPanel.placeHolder")}
                    value={searchParam}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                />
                {loading && (
                    <div className="search-clear">
                        <span className="spinner-small"></span>
                    </div>
                )}
                {!loading && searchParam && (
                    <div className="search-clear" onClick={onClear}>
                        <span className="icon-geolite-close"></span>
                    </div>
                )}
            </div>
            <div className="search-button" onClick={executeSearch} title={i18next.t("searchPanel.placeHolder")}>
                <span className="icon-geolite-search"></span>
            </div>
            {resultFeatures.length ? (
                <div className="search-button" onClick={clearResults} title={i18next.t("searchPanel.clear")}>
                    <span className="icon-geolite-trash"></span>
                </div>
            ) : null}
        </div>
    );
};
